/**
 * The realtime wire protocol, shared by both ends so a frame the server writes and a
 * frame the client reads cannot drift apart.
 *
 * One socket per client, carrying named topics. `phase` is the only one today; `chat:<leagueId>`
 * is the reason the envelope has a `topic` field at all rather than being a bare phase payload.
 *
 * ## The `seq` is the catch-up mechanism
 *
 * `EventSource` gave `Last-Event-ID` for free; a WebSocket gives nothing, so every frame carries a
 * per-topic monotonic `seq` and the client echoes the last one it saw as `since` when it
 * re-subscribes. For `phase` the server can answer *any* `since` by sending current state — the
 * state is the whole payload, so there is no backlog to store and a reconnect self-heals. A topic
 * with real history (chat) will need a stored backlog; that is noted in the broker, not built here.
 */

/** Every topic the broker knows. Adding one here is the only edit a new feed needs on the wire. */
export type RealtimeTopic = 'phase'

export const REALTIME_PATH = '/api/realtime'

/** Server → client. `payload` is topic-specific; `phase` sends `RealtimePhasePayload`. */
export interface RealtimeFrame<T = unknown> {
  topic: RealtimeTopic
  seq: number
  payload: T
}

/** Client → server. The only message the client sends today, and the reason this is a WebSocket. */
export interface RealtimeSubscribe {
  type: 'subscribe'
  topic: RealtimeTopic
  /** Highest `seq` already applied, so a reconnect does not re-apply what it holds. */
  since?: number
}

export type RealtimeClientMessage = RealtimeSubscribe

/** The `phase` payload: exactly what `GET /api/gameweek-phase` returns, so the client's re-sync
 *  path is identical whether the phases arrived over the socket or over the fallback poll. */
export interface RealtimePhaseRow {
  gameweek: number
  deadlinePassed: boolean
  resultsRevealed: boolean
  scoredDigest: string | null
}
export type RealtimePhasePayload = RealtimePhaseRow[]

/**
 * Anything larger is refused and the socket closed. Dead weight while the client only ever sends
 * `subscribe`, and the first thing that matters the day a chat message can be typed into it.
 */
export const MAX_CLIENT_MESSAGE_BYTES = 4096

/** Close codes we send. 1008 is the one with a rule attached — see `canReconnect`. */
export const CLOSE_POLICY_VIOLATION = 1008
export const CLOSE_UNSUPPORTED_DATA = 1003

/**
 * Whether a close should feed the client's reconnect ladder.
 *
 * **1008 must not.** A client refused for policy (no session, not allowed on the topic) that
 * reconnects will be refused again, immediately, forever — a rejected client would hammer the
 * socket as fast as the backoff allows. Everything else (a redeploy, a dropped connection, an
 * idle-timeout close) is transient and should reconnect.
 */
export function canReconnect(code: number): boolean {
  return code !== CLOSE_POLICY_VIOLATION && code !== CLOSE_UNSUPPORTED_DATA
}

export function isRealtimeFrame(value: unknown): value is RealtimeFrame {
  if (typeof value !== 'object' || value === null) return false
  const frame = value as Partial<RealtimeFrame>
  return frame.topic === 'phase' && typeof frame.seq === 'number' && 'payload' in frame
}
